/**
 * variant-image-filter.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Filters the product media gallery to show only images matching the selected
 * color variant. Reads image color from alt text using the following rules:
 *
 *   Alt text format          → Color extracted
 *   ─────────────────────────────────────────
 *   "Matte Black"            → "Matte Black"
 *   "White / Red"            → "White / Red"
 *   "Helmet - Matte Black"   → "Matte Black"
 *   "Helmet - White / Red"   → "White / Red"
 *   ""  (blank)              → always shown (treated as shared/lifestyle image)
 *
 * Fallback: if filtering would result in zero visible images (e.g. no alt tags
 * have been set), ALL images are shown instead of leaving the gallery empty.
 *
 * Listens to the `variant:changed` window event dispatched by variant-monitor.js.
 * Applies immediately on DOMContentLoaded using the pre-selected variant.
 *
 * Only activates for products that have a "Color" option AND multiple variants.
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // ── Configuration ──────────────────────────────────────────────────────────
  const CONFIG = {
    /** The Shopify option name to match against (case-insensitive). */
    colorOptionName: 'Color',

    /** Separator between product name and color in alt text. */
    altSeparator: ' - ',

    /**
     * Images with blank alt text are always shown (treated as shared/lifestyle
     * images that apply to all colors).
     */
    showBlankAlt: true,

    /** CSS class applied to hidden slides and thumbnail items. */
    hiddenClass: 'vif-hidden',
  };

  // ── Inject hide CSS once ────────────────────────────────────────────────────
  (function injectStyles() {
    if (document.getElementById('vif-styles')) return;
    const style = document.createElement('style');
    style.id = 'vif-styles';
    style.textContent = `.${CONFIG.hiddenClass} { display: none !important; }`;
    document.head.appendChild(style);
  })();

  // ── Helpers ─────────────────────────────────────────────────────────────────

  /**
   * Parse the color out of an image alt text.
   * If alt contains " - ", use everything after the LAST " - ".
   * Otherwise use the entire alt text.
   */
  function parseColorFromAlt(alt) {
    if (!alt || !alt.trim()) return '';
    const sep = CONFIG.altSeparator;
    const lastIdx = alt.lastIndexOf(sep);
    return lastIdx !== -1
      ? alt.slice(lastIdx + sep.length).trim()
      : alt.trim();
  }

  /**
   * Case-insensitive, whitespace-trimmed color comparison.
   */
  function colorsMatch(altColor, selectedColor) {
    return altColor.toLowerCase() === selectedColor.toLowerCase();
  }

  // ── Main class ───────────────────────────────────────────────────────────────

  class VariantImageFilter {
    /**
     * @param {string} sectionId  - The Shopify section ID (e.g. "main-product")
     * @param {string[]} optionNames - Ordered array of product option names
     */
    constructor(sectionId, optionNames) {
      this.sectionId = sectionId;
      this.optionNames = optionNames;

      // Find which index in variant.options[] corresponds to "Color"
      this.colorIndex = optionNames.findIndex(
        (name) => name.toLowerCase() === CONFIG.colorOptionName.toLowerCase()
      );

      // No Color option on this product — nothing to do
      if (this.colorIndex === -1) return;

      // Gallery viewer slides: li[data-media-id][data-media-alt]
      this.viewerSlides = Array.from(
        document.querySelectorAll(
          `#Slider-Gallery-${sectionId} li[data-media-id]`
        )
      );

      // Thumbnail strip items: li[data-target][data-media-alt]
      this.thumbnailItems = Array.from(
        document.querySelectorAll(
          `#Slider-Thumbnails-${sectionId} li[data-target]`
        )
      );

      // The media-gallery custom element (used to activate a slide)
      this.mediaGallery = document.getElementById(`MediaGallery-${sectionId}`);

      // Listen for variant changes (dispatched by variant-monitor.js)
      window.addEventListener('variant:changed', this._onVariantChanged.bind(this));
    }

    /**
     * Handle the variant:changed event.
     */
    _onVariantChanged(event) {
      const variant = event.detail && event.detail.variant;
      if (!variant) {
        // No variant → show everything
        this._showAll();
        return;
      }

      const selectedColor = variant.options && variant.options[this.colorIndex];
      if (!selectedColor) {
        this._showAll();
        return;
      }

      this._applyFilter(selectedColor);
    }

    /**
     * Show/hide slides and thumbnails based on the selected color.
     * Images with blank alt are always shown.
     */
    _applyFilter(selectedColor) {
      let firstVisibleMediaId = null;
      let visibleSlideCount = 0;

      // ── Filter viewer slides ───────────────────────────────────────────────
      this.viewerSlides.forEach((slide) => {
        const alt = slide.dataset.mediaAlt || '';
        const shouldShow = this._shouldShow(alt, selectedColor);

        slide.classList.toggle(CONFIG.hiddenClass, !shouldShow);

        if (shouldShow) {
          visibleSlideCount++;
          if (firstVisibleMediaId === null) {
            firstVisibleMediaId = slide.dataset.mediaId;
          }
        }
      });

      // ── Fallback: if no slides matched, show everything ────────────────────
      if (visibleSlideCount === 0) {
        console.info(
          `[variant-image-filter] No images matched color "${selectedColor}" — showing all images as fallback.`
        );
        this._showAll();
        return;
      }

      // ── Filter thumbnail strip ─────────────────────────────────────────────
      this.thumbnailItems.forEach((thumb) => {
        const alt = thumb.dataset.mediaAlt || '';
        const shouldShow = this._shouldShow(alt, selectedColor);
        thumb.classList.toggle(CONFIG.hiddenClass, !shouldShow);
      });

      // ── Ensure a visible slide is active ───────────────────────────────────
      if (firstVisibleMediaId && this.mediaGallery) {
        // Check if the currently active slide is now hidden
        const activeSlide = this.mediaGallery.querySelector(
          `li[data-media-id].is-active`
        );
        const activeIsHidden =
          activeSlide && activeSlide.classList.contains(CONFIG.hiddenClass);

        if (activeIsHidden || !activeSlide) {
          // Activate the first visible slide
          this.mediaGallery.setActiveMedia?.(firstVisibleMediaId, true);
        }
      }
    }

    /**
     * Determine if a slide should be visible for the given color selection.
     * Blank alt → always visible (lifestyle/shared images).
     */
    _shouldShow(alt, selectedColor) {
      if (!alt || !alt.trim()) {
        return CONFIG.showBlankAlt;
      }
      const altColor = parseColorFromAlt(alt);
      return colorsMatch(altColor, selectedColor);
    }

    /**
     * Remove the hidden class from all slides and thumbnails.
     */
    _showAll() {
      [...this.viewerSlides, ...this.thumbnailItems].forEach((el) => {
        el.classList.remove(CONFIG.hiddenClass);
      });
    }
  }

  // ── Bootstrap ────────────────────────────────────────────────────────────────

  /**
   * Find all media-gallery elements on the page and initialize a filter for
   * each one that has product metadata and multiple color variants.
   */
  function init() {
    document.querySelectorAll('media-gallery[id^="MediaGallery-"]').forEach((gallery) => {
      // Extract the section ID from the element's ID ("MediaGallery-{sectionId}")
      const sectionId = gallery.id.replace('MediaGallery-', '');

      // Read product metadata embedded by Liquid
      const metaEl = document.getElementById(`ProductMediaData-${sectionId}`);
      if (!metaEl) return;

      let meta;
      try {
        meta = JSON.parse(metaEl.textContent);
      } catch (e) {
        console.warn('[variant-image-filter] Could not parse product metadata:', e);
        return;
      }

      // Only activate for products with a Color option AND multiple variants
      if (!meta.hasMultipleColorVariants) return;

      // Instantiate the filter
      const filter = new VariantImageFilter(sectionId, meta.optionNames);

      // Apply the filter immediately for the currently selected variant
      // (handles page loads with ?variant= URL params)
      const selectedVariantEl = gallery
        .closest('product-info')
        ?.querySelector('[data-selected-variant]');

      if (selectedVariantEl) {
        let selectedVariant;
        try {
          selectedVariant = JSON.parse(selectedVariantEl.textContent);
        } catch (_) {
          // silently ignore
        }

        if (selectedVariant) {
          // Dispatch a synthetic event so the filter runs with the initial variant
          filter._onVariantChanged({ detail: { variant: selectedVariant, isInitial: true } });
        }
      }
    });
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
